import { DishAllergens } from './DishAllergens'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { omitTransientProps } from '../../lib/omitTransientProps'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalAlert,
  MinimalBadge,
  MinimalButton,
  MinimalCard,
  MinimalDropdown,
  MinimalEmptyState,
  MinimalFieldGrid,
  MinimalFormSection,
  MinimalInput,
  MinimalNumberField,
  MinimalOption,
  MinimalSegmentedControl,
} from '@ovasabi/ui-minimal'
import { DatePickerField, Money, PageHeader, Screen, SectionHeading } from '../../components/chow'
import { PlateIcon } from '../../components/icons'
import {
  useChefMenuSnapshot,
  useDishSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { allowSeedContent, commandPayload, commands, isLiveRuntime, mediaUrl } from '../../lib/runtime'
import {
  ChefStyle,
  echoUpsert,
  confirmedUpsert,
  enumLabel,
  enumShort,
  isoMonday,
  isoUpcomingMonday,
  type StoreRecord,
} from './chefShared'
import { CoverPicker, useImagePicker } from './imagePicker'
import {
  citiesForState,
  composeRegionTag,
  inferRegion,
  NIGERIAN_STATES,
} from './regions'
import { useChooseChow, usePageContext } from './ChooseChowContext'
import { canonicalDishIds, menuDishLibrary } from './dishLibrary'
import { useAuth } from './auth/AuthContext'
import { CategoryPicker, CategoryRequestForm, KitchenLocationRequired, kitchenAddress, useKitchenCatalog } from './kitchenCatalog'

/**
 * Create or edit a menu from reusable library dishes.
 * Save details, schedule, selection, and publication in one backend transaction.
 */

type DishRecord = StoreRecord

/** Reduce a transport/command failure to one actionable line for the chef. */
const optionalNumber = (value: unknown): number | null => value == null || value === '' ? null : Number(value)

const saveErrorMessage = (error: unknown, subject: 'menu' | 'dish'): string => {
  const detail = error instanceof Error ? error.message.trim() : String(error ?? '').trim()
  return detail
    ? `Could not save this ${subject}: ${detail}. Your changes are still here — try again.`
    : `Could not save this ${subject}. Check your connection and try again — your changes are still here.`
}

const CADENCES: readonly MinimalOption<'weekly' | 'daily' | 'monthly' | 'one_off'>[] = [
  { value: 'weekly', label: 'Every week (Weekly)' },
  { value: 'daily', label: 'Every day (Daily)' },
  { value: 'monthly', label: 'Every month (Monthly)' },
  { value: 'one_off', label: 'One-time menu' },
]

const DISH_CATEGORIES: readonly MinimalOption<string>[] = [
  { value: 'main', label: 'Main' },
  { value: 'rice', label: 'Rice' },
  { value: 'soups', label: 'Soups' },
  { value: 'protein', label: 'Protein' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'grill', label: 'Grill' },
  { value: 'pastry', label: 'Pastry' },
  { value: 'drinks', label: 'Drinks' },
  { value: 'sides', label: 'Sides' },
]


/** Where the dish is cooked — the states this deployment delivers in. */
const REGION_STATES: readonly MinimalOption<string>[] = NIGERIAN_STATES.map((state) => ({
  value: state.name,
  label: state.name === 'FCT' ? 'FCT (Abuja)' : state.name,
}))

const STEP_OPTIONS: readonly MinimalOption<'details' | 'dishes' | 'review'>[] = [
  { value: 'details', label: 'Details' },
  { value: 'dishes', label: 'Dishes' },
  { value: 'review', label: 'Review' },
]

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  > * { min-width: 0; }
  gap: ${minimalVars.space.sm};
`

/* Sticky cart-style action bar pinned to the bottom of the screen.
   - ~92% surface opacity + backdrop blur so the page peeks through.
   - Shadow + divider only when there's still content scrolling under the bar
     (the EndSentinel is OFF-screen). Once the user scrolls to the bottom the
     sentinel intersects the viewport and we drop the elevation — flat finish. */
// motion would pass `$` props to the DOM; styled-components used to strip them.
const ActionBar = styled(omitTransientProps(motion.div))<{ $elevated: boolean }>`
  position: sticky;
  bottom: 0;
  margin: 0 calc(-1 * ${minimalVars.space.md});
  padding: ${minimalVars.space.xs} ${minimalVars.space.md};
  /* surface @ 92% with a blur backdrop so scrolling content shows through. */
  background: color-mix(in srgb, ${minimalVars.color.bgSurface} var(--chow-glass-surface-mix, 92%), transparent);
  backdrop-filter: var(--chow-backdrop-filter, blur(8px) saturate(140%));
  -webkit-backdrop-filter: var(--chow-backdrop-filter, blur(8px) saturate(140%));
  border-top: 1px solid
    ${({ $elevated }) => ($elevated ? minimalVars.color.borderSubtle : 'transparent')};
  box-shadow: ${({ $elevated }) =>
    $elevated ? 'var(--chow-shadow-action-bar, 0 -8px 24px rgba(0, 0, 0, 0.08))' : 'none'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  z-index: 2;
  transition:
    box-shadow 200ms ease,
    border-color 200ms ease,
    background 200ms ease;
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
  }
`

/** Invisible 1px target placed right before the ActionBar — when it crosses
 *  into the viewport, the user has reached the bottom and the bar can de-emphasize. */
const EndSentinel = styled.div`
  height: 1px;
  width: 100%;
`

/**
 * useScrollEnd — true once `sentinelRef` enters the viewport. Drives the
 * action bar's elevation: stays elevated while content scrolls under it,
 * goes flat the moment the user reaches the bottom.
 */
const useScrollEnd = (sentinelRef: React.RefObject<HTMLDivElement | null>) => {
  const [reached, setReached] = useState(false)
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setReached(entry.isIntersecting)
        }
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [sentinelRef])
  return reached
}

const ActionBarMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const ActionBarActions = styled.div`
  display: flex;
  gap: ${minimalVars.space.xs};
  flex-shrink: 0;

  /* 560px matches no named stop; it sits between the hand and page stops. */
  @media (max-width: 560px) {
    width: 100%;
    flex-wrap: wrap;

    > button {
      flex: 1 1 140px;
    }
  }
`

/* Section divider between the dish library and the new-dish form. */
const SectionDivider = styled.div`
  height: 1px;
  background: ${minimalVars.color.borderSubtle};
  margin: ${minimalVars.space.xs} 0;
`

const PickerRow = styled(MinimalButton)<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  align-items: center;
  gap: ${minimalVars.space.xs};
  width: 100%;
  padding: ${minimalVars.space.xs};
  min-height: 82px;
  border-radius: ${minimalVars.radius.sm};
  border: 1.5px solid
    ${({ $selected }) => ($selected ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
  background: ${({ $selected }) =>
    $selected ? `color-mix(in srgb, ${minimalVars.color.brand} 6%, transparent)` : minimalVars.color.bgSurface};
  cursor: pointer;
  text-align: left;
  transition:
    border-color 160ms ease,
    background 160ms ease;

  /* 560px matches no named stop; it sits between the hand and page stops. */
  @media (max-width: 560px) {
    grid-template-columns: 52px minmax(0, 1fr);

    > :nth-last-child(2),
    > :last-child {
      grid-column: 2;
      justify-self: start;
    }
  }
`

const Thumb = styled.div<{ $src?: string }>`
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: ${minimalVars.radius.sm};
  background-color: ${minimalVars.color.bgSurface};
  background-image: ${({ $src }) => {
    const resolved = mediaUrl($src, 128)
    return resolved ? `url(${resolved})` : 'none'
  }};
  background-size: cover;
  background-position: center;
  border: 1px solid ${minimalVars.color.borderSubtle};
`

const variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

/**
 * Pick existing dishes from the chef's library (any dish previously created
 * across any menu the chef owns). Toggling a row marks it for inclusion.
 */
const DishPicker = ({
  library,
  selectedIds,
  onToggle,
}: {
  library: readonly DishRecord[]
  selectedIds: ReadonlySet<string>
  onToggle: (id: string) => void
}) => {
  if (library.length === 0) {
    return (
      <MinimalEmptyState
        icon={<PlateIcon />}
        title="No dishes yet"
        description="Add your first dish below — it'll be saved to your library and reusable on future menus."
      />
    )
  }
  return (
    <ChefStyle.Stack>
      {library.map((dish) => {
        const id = String(dish.id ?? '')
        const selected = selectedIds.has(id)
        return (
          <PickerRow variant="quiet"
            key={id}
            type="button"
            $selected={selected}
            onClick={() => onToggle(id)}
            aria-pressed={selected}
          >
            <Thumb $src={dish.image_url ? String(dish.image_url) : undefined} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <ChefStyle.Ref>{String(dish.name ?? '')}</ChefStyle.Ref>
              <br />
              <ChefStyle.Sub>{enumLabel(dish.category, 'main')}</ChefStyle.Sub>
            </div>
            <Money amount={Number(dish.price_minor ?? 0) / 100} tone="brand" size="sm" />
            <MinimalBadge tone={selected ? 'brand' : 'neutral'}>
              {selected ? 'Added' : 'Add'}
            </MinimalBadge>
          </PickerRow>
        )
      })}
    </ChefStyle.Stack>
  )
}

/** Inline "create a new dish" form for the dish library. Submits standalone. */
const NewDishForm = ({
  chefProfileId,
  onCreated,
  existingNames,
  categoryOptions,
}: {
  chefProfileId: string
  onCreated: (dish: DishRecord) => void
  categoryOptions: readonly MinimalOption<string>[]
  existingNames: readonly string[]
}) => {
  const { user } = useAuth()
  const live = isLiveRuntime() || !allowSeedContent() || Boolean(user?.id)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('main')
  const [price, setPrice] = useState('')
  const [description, setDescription] = useState('')
  const [ingredients, setIngredients] = useState('')
  const [cookTime, setCookTime] = useState('')
  const [allergens, setAllergens] = useState<ReadonlySet<string>>(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | undefined>()
  const photo = useImagePicker('dish', chefProfileId)

  const vendors = useVendorProfileSnapshot() as { records: readonly StoreRecord[] }
  const myVendor = useMemo(
    () => vendors.records.find((v) => String(v.profile_id ?? '') === chefProfileId),
    [vendors.records, chefProfileId],
  )
  const inferred = useMemo(
    () => inferRegion(String(myVendor?.location_label ?? ''), String(myVendor?.cuisine ?? '')),
    [myVendor],
  )
  const [regionState, setRegionState] = useState(inferred.state)
  const [regionCity, setRegionCity] = useState(inferred.city)
  const regionTouched = useRef(false)
  useEffect(() => {
    if (regionTouched.current) return
    setRegionState(inferred.state)
    setRegionCity(inferred.city)
  }, [inferred])

  const cityOptions = useMemo<readonly MinimalOption<string>[]>(
    () => citiesForState(regionState).map((city) => ({ value: city, label: city })),
    [regionState],
  )

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const priceMinor = Math.round(Number(price) * 100)
    if (!name.trim() || price.trim() === '' || allergens.size === 0 || !Number.isFinite(priceMinor) || priceMinor < 0) {
      setErr('Name, price, and an allergen selection are required.')
      return
    }
    if (existingNames.includes(name.trim())) {
      setErr('A dish with this name already exists. Select it from your library.')
      return
    }
    setErr(undefined)
    setSubmitting(true)
    try {
      const payload: StoreRecord = {
        chef_profile_id: chefProfileId,
        name: name.trim(),
        description: description.trim(),
        category,
        price_minor: priceMinor,
        currency: 'NGN',
      }
      // region_tag is the publish gate: without it (and at least one allergen)
      // the server stores the dish as `hidden` and it never reaches customers.
      payload.region_tag = composeRegionTag(regionCity, regionState)
      if (photo.url) payload.image_url = photo.url
      const ingredientList = ingredients
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
      if (ingredientList.length > 0) payload.ingredients = ingredientList
      if (allergens.size > 0) payload.allergens = [...allergens]
      const cookMinutes = Math.round(Number(cookTime))
      if (Number.isFinite(cookMinutes) && cookMinutes > 0) payload.cook_time_minutes = cookMinutes

      let created: DishRecord
      try {
        const result = await commands.menu.createDish(payload)
        const saved = commandPayload<DishRecord>(result)
        if (live && !saved.id) throw new Error('We couldn’t confirm the dish was saved. Try again.')
        created = saved.id
          ? saved
          : {
              ...payload,
              id: `live-${Date.now()}`,
              menu_id: '',
              status: 'available',
            }
      } catch (error) {
        // Live mode: don't clear the form and pretend the dish saved when it
        // didn't — surface the reason so the chef can fix it and retry.
        if (live) {
          setErr(saveErrorMessage(error, 'dish'))
          return
        }
        created = {
          ...payload,
          id: `local-${Date.now()}`,
          menu_id: '',
          status: 'available',
        }
      }
      confirmedUpsert('menu.dishes', created)
      onCreated(created)
      setName('')
      setDescription('')
      setPrice('')
      setAllergens(new Set())
      setIngredients('')
      setCookTime('')
      photo.reset()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <MinimalFormSection
      title="New dish"
      description="Saved to your dishes, so you can use it on any menu."
    >
      <form onSubmit={submit}>
        <MinimalFieldGrid>
          <MinimalInput
            label="Dish name"
            required
            hint="Dish names are unique in your library."
            placeholder="e.g. Smoky Jollof Rice"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <CategoryPicker
            options={categoryOptions}
            value={category}
            onChange={setCategory}
          />
          <MinimalInput
            label="Price (₦)"
            required
            hint="What the customer pays, in naira, including any VAT you charge. ChooseChow adds only its service fee."
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            placeholder="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <MinimalInput
            label="Description"
            placeholder="Optional"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <MinimalInput
            label="Main ingredients"
            placeholder="e.g. rice, tomatoes, smoked fish"
            value={ingredients}
            onChange={(e) => setIngredients(e.target.value)}
          />
          <MinimalNumberField label="Cook time (min)" min={1} max={1440} step={1}
            placeholder="Optional" value={cookTime === '' ? null : Number(cookTime)}
            onValueChange={(value) => setCookTime(value == null ? '' : String(value))} />
          <MinimalDropdown
            label={<>State <span aria-hidden="true">*</span></>}
            options={REGION_STATES}
            value={regionState}
            onChange={(next) => {
              regionTouched.current = true
              setRegionState(next)
              setRegionCity(citiesForState(next)[0] ?? '')
            }}
          />
          <MinimalDropdown
            label={<>City / area <span aria-hidden="true">*</span></>}
            options={cityOptions}
            value={regionCity}
            onChange={(next) => {
              regionTouched.current = true
              setRegionCity(next)
            }}
          />
        </MinimalFieldGrid>
        <DishAllergens value={allergens} onChange={setAllergens} />
        <div style={{ marginTop: '0.75rem' }}>
          <CoverPicker
            picker={photo}
            size={200}
            ratio="4 / 3"
            label="Add photo"
            glyph="🍽️"
            helper="Optional. It shows as a wide rectangle wherever people browse this dish, so keep the food in the middle."
          />
        </div>
        {err && (
          <MinimalAlert tone="danger" style={{ marginTop: '0.75rem' }}>
            {err}
          </MinimalAlert>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <MinimalButton type="submit" disabled={submitting || photo.uploading || Boolean(photo.error)}>
            {submitting ? 'Adding…' : 'Add to library'}
          </MinimalButton>
        </div>
      </form>
    </MinimalFormSection>
  )
}

/** The full builder screen. */
export const MenuBuilderPage = () => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const { user } = useAuth()
  const live = isLiveRuntime() || !allowSeedContent() || Boolean(user?.id)
  const kitchen = useKitchenCatalog(profileId, live)
  const [locationId, setLocationId] = useState('')
  const [stockQuantities, setStockQuantities] = useState<Record<string, string>>({})
  const page = usePageContext('/menu/new')
  const { menuId } = useParams<{ menuId?: string }>()
  const editing = Boolean(menuId)

  // Read whole stores; filter to the chef's records in dummy mode all rows
  // show, in live mode strict by chef_profile_id (matches ChefMenuHub).
  const menusSnap = useChefMenuSnapshot() as { records: readonly StoreRecord[] }
  const dishesSnap = useDishSnapshot() as { records: readonly StoreRecord[] }
  const [loadedMenu, setLoadedMenu] = useState<StoreRecord>()
  const [loadedDishes, setLoadedDishes] = useState<readonly StoreRecord[]>([])
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(live ? 'loading' : 'ready')
  const [loadAttempt, setLoadAttempt] = useState(0)
  const existingMenu = loadedMenu ?? (
    editing ? menusSnap.records.find((m) => String(m.id) === menuId) : undefined
  )

  const [step, setStep] = useState<'details' | 'dishes' | 'review'>('details')
  const [title, setTitle] = useState(String(existingMenu?.title ?? ''))
  const [summary, setSummary] = useState(String(existingMenu?.summary ?? ''))
  const [cadence, setCadence] = useState<'weekly' | 'daily' | 'monthly' | 'one_off'>(
    (enumShort(existingMenu?.cadence, 'weekly') as 'weekly' | 'daily' | 'monthly' | 'one_off'),
  )
  // For a NEW menu, default the anchor to the upcoming Monday (today if it's
  // already Monday). An existing menu may carry a past week_start — we
  // preserve it verbatim so the chef can edit historical menus, but the
  // calendar disables past days for selection.
  const [weekStart, setWeekStart] = useState<string>(
    String(existingMenu?.week_start ?? isoUpcomingMonday()),
  )
  const [selectedDishes, setSelectedDishes] = useState<ReadonlySet<string>>(() => new Set())
  const selectionSeeded = useRef(false)
  const saveKey = useRef(`menu-save-${crypto.randomUUID()}`)
  const saving = useRef(false)
  // Seeded from the menu being edited so the frame shows its current cover and
  // offers "Replace photo", instead of an empty box on a menu that has one.
  const cover = useImagePicker(
    'menu',
    profileId,
    existingMenu?.cover_image_url ? String(existingMenu.cover_image_url) : undefined,
  )
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | undefined>()
  const dishesEndRef = useRef<HTMLDivElement | null>(null)
  const reviewEndRef = useRef<HTMLDivElement | null>(null)
  const dishesElevated = !useScrollEnd(dishesEndRef)
  const reviewElevated = !useScrollEnd(reviewEndRef)

  const library = useMemo(
    () =>
      menuDishLibrary([...new Map([...dishesSnap.records, ...loadedDishes].map((dish) => [dish.id, dish])).values()].filter((d) => {
        const owner = String(d.chef_profile_id ?? '')
        return Boolean(profileId) && owner === profileId
      }), menuId),
    [dishesSnap.records, loadedDishes, profileId, live, menuId],
  )

  useEffect(() => {
    if (!live) return
    let active = true
    setLoadState('loading')
    setErr(undefined)
    const load = async () => {
      try {
        const [dishResponse, menuResponse, attachedResponse] = await Promise.all([
          commands.menu.listChefDishes(profileId),
          editing ? commands.menu.listChefMenus(profileId) : Promise.resolve({ menus: [] }),
          menuId ? commands.menu.listDishes(menuId) : Promise.resolve({ dishes: [] }),
        ])
        if (!active) return
        const dishRows = commandPayload(dishResponse).dishes
        const menus = commandPayload(menuResponse).menus
        const attached = commandPayload(attachedResponse).dishes
        if (!Array.isArray(dishRows) || !Array.isArray(menus) || !Array.isArray(attached)) {
          throw new Error('Part of this menu didn’t load. Reload the page before saving.')
        }
        const menu = menus.find((row: StoreRecord) => String(row.id) === menuId)
        if (editing && (!menu || String(menu.chef_profile_id) !== profileId)) {
          throw new Error('This menu is unavailable or belongs to another kitchen.')
        }
        const rows = [...dishRows, ...attached] as StoreRecord[]
        setLoadedDishes(rows)
        if (menu) {
          setLoadedMenu(menu)
          setLocationId(String(menu.location_id ?? ''))
          setStockQuantities(Object.fromEntries(attached.map((dish: StoreRecord) => [String(dish.id), dish.stock_quantity == null ? '' : String(dish.stock_quantity)])))
          setTitle(String(menu.title ?? ''))
          setSummary(String(menu.summary ?? ''))
          setCadence(enumShort(menu.cadence, 'weekly') as typeof cadence)
          setWeekStart(String(menu.week_start || isoUpcomingMonday()))
          setSelectedDishes(canonicalDishIds(attached, menuDishLibrary(rows, menuId)))
        }
        selectionSeeded.current = true
        setLoadState('ready')
      } catch (error) {
        if (active) {
          setErr(error instanceof Error ? error.message : 'Could not load the menu.')
          setLoadState('error')
        }
      }
    }
    void load()
    return () => { active = false }
  }, [editing, loadAttempt, menuId, profileId, live])

  useEffect(() => {
    if (live || !editing || selectionSeeded.current || !existingMenu) return
    setTitle(String(existingMenu.title ?? ''))
    setSummary(String(existingMenu.summary ?? ''))
    setCadence(enumShort(existingMenu.cadence, 'weekly') as typeof cadence)
    setWeekStart(String(existingMenu.week_start || isoUpcomingMonday()))
    const attached = dishesSnap.records.filter((dish) => String(dish.menu_id) === menuId)
    setSelectedDishes(canonicalDishIds(attached, library))
    selectionSeeded.current = true
  }, [dishesSnap.records, editing, existingMenu, library, menuId, live])

  const selectedDishRecords = useMemo(
    () => library.filter((d) => selectedDishes.has(String(d.id))),
    [library, selectedDishes],
  )

  const total = selectedDishRecords.reduce(
    (sum, d) => sum + Number(d.price_minor ?? 0),
    0,
  )

  const toggleDish = (id: string) =>
    setSelectedDishes((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleNewDish = (dish: DishRecord) => {
    setLoadedDishes((rows) => [...rows, dish])
    setSelectedDishes((prev) => {
      const next = new Set(prev)
      next.add(String(dish.id))
      return next
    })
  }

  const saveMenu = async (status: string) => {
    if (saving.current || loadState !== 'ready') return
    if (cover.uploading || cover.error) {
      setErr(cover.error || 'Wait for the cover photo to finish uploading')
      return
    }
    if (!title.trim() || (cadence === 'weekly' && !weekStart)) {
      setErr('Add a title and a valid start date before saving')
      setStep('details')
      return
    }
    const stock: Record<string, number | null> = {}
    for (const id of selectedDishes) {
      const value = stockQuantities[id] ?? ''
      if (value !== '' && (!Number.isInteger(Number(value)) || Number(value) < 0 || Number(value) > 1000000)) { setErr('Stock must be a whole number between 0 and 1000000.'); return }
      stock[id] = value === '' ? null : Number(value)
    }
    if (live && !locationId && kitchen.locations.length === 0) { setErr('Add where you cook first, under Profile → Kitchen locations.'); return }
    saving.current = true
    setSubmitting(true)
    setErr(undefined)
    const payload: StoreRecord = {
      chef_profile_id: profileId, title: title.trim(), summary: summary.trim(),
      cadence, week_start: cadence === 'weekly' ? weekStart : '', currency: 'NGN',
      cover_image_url: cover.changed ? cover.url ?? '' : String(existingMenu?.cover_image_url ?? ''),
      dish_ids: [...selectedDishes], status,
      location_id: locationId || String(kitchen.locations.find((location) => location.is_primary)?.id ?? kitchen.locations[0]?.id ?? ''),
      stock_quantities: stock,
    }
    try {
      const response = menuId
        ? await commands.menu.updateMenu(menuId, payload, saveKey.current)
        : await commands.menu.createMenu(payload, saveKey.current)
      const saved = commandPayload<StoreRecord>(response)
      if (!saved.id || !Array.isArray(saved.dishes) || !Array.isArray(saved.detached_dishes)) {
        throw new Error('We couldn’t confirm your menu was saved. Reload the page, then try again.')
      }
      confirmedUpsert('menu.chef_menus', saved)
      for (const dish of [...saved.dishes, ...saved.detached_dishes] as StoreRecord[]) {
        confirmedUpsert('menu.dishes', dish)
      }
      navigate('/menu')
    } catch (error) {
      if (live) {
        setErr(saveErrorMessage(error, 'menu'))
      } else {
        const id = menuId ?? `local-${Date.now()}`
        echoUpsert('menu.chef_menus', { ...payload, id })
        const names = new Set(selectedDishRecords.map((dish) => dish.name))
        for (const dish of dishesSnap.records.filter((row) => row.menu_id === id && !names.has(row.name))) {
          echoUpsert('menu.dishes', { ...dish, menu_id: '', status: 'hidden' })
        }
        for (const dish of selectedDishRecords) {
          echoUpsert('menu.dishes', { ...dish, id: `local-${id}-${dish.id}`, menu_id: id })
        }
        navigate('/menu')
      }
    } finally {
      saving.current = false
      setSubmitting(false)
    }
  }

  const saveDraft = () => saveMenu(editing ? enumShort(existingMenu?.status, 'draft') : 'draft')
  const publish = () => saveMenu('published')

  const goNext = () => setStep((s) => (s === 'details' ? 'dishes' : 'review'))
  const goBack = () => setStep((s) => (s === 'review' ? 'dishes' : 'details'))

  if (live && (kitchen.loading || kitchen.error || kitchen.locations.length === 0)) return (
    <Screen header={<PageHeader title="Kitchen setup" />}><KitchenLocationRequired loading={kitchen.loading} error={kitchen.error} reload={kitchen.reload} /></Screen>
  )

  if (loadState !== 'ready') return (
    <Screen header={<PageHeader title={editing ? 'Edit menu' : page.title} />}>
      <MinimalAlert tone={loadState === 'error' ? 'danger' : 'info'}>{err || 'Loading menu and dishes…'}</MinimalAlert>
      {loadState === 'error' && <MinimalButton onClick={() => setLoadAttempt((n) => n + 1)}>Retry loading menu</MinimalButton>}
    </Screen>
  )

  return (
    <Screen
      header={
        <PageHeader
          title={editing ? 'Edit menu' : page.title}
          accentDot
        />
      }
    >
      <MinimalSegmentedControl
        ariaLabel="Builder step"
        options={STEP_OPTIONS}
        value={step}
        onChange={(next) => setStep(next)}
      />

      <motion.div
        key={step}
        initial="hidden"
        animate="visible"
        variants={variants}
        transition={{ duration: 0.22, ease: 'easeOut' }}
      >
        {step === 'details' && (
          <StepGrid>
            <CoverPicker
              picker={cover}
              helper="Optional. It shows as a wide rectangle on your menu card, so keep the food in the middle."
            />
            <MinimalFormSection
              title="Menu details"
              description="Set your schedule. Publish when you are ready for customers to order."
            >
              {/* One consistent single column: every field is full-width with a
                  uniform gap, so nothing is half-width or width-dependent. */}
              <MinimalFieldGrid columns={1}>
                {live && <MinimalDropdown label={<>Kitchen location <span aria-hidden="true">*</span></>}
                  options={kitchen.locations.map((location) => ({ value: location.id, label: `${location.label} · ${kitchenAddress(location)}` }))}
                  value={locationId || String(kitchen.locations.find((location) => location.is_primary)?.id ?? kitchen.locations[0]?.id ?? '')}
                  onChange={setLocationId} hint="This location supplies the menu and its pickup address." />}

                <MinimalInput
                  label="Title"
                  required
                  hint="Give this menu a clear name."
                  placeholder="e.g. Lunch Box Special"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <MinimalDropdown
                  label={<>Menu cadence <span aria-hidden="true">*</span></>}
                  options={CADENCES}
                  value={cadence}
                  onChange={(next) => setCadence(next)}
                />
                <MinimalInput
                  label="Menu summary"
                  placeholder="What people get, e.g. 5 home-cooked lunches, Monday to Friday"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
                {cadence === 'weekly' && (
                  <DatePickerField
                    label="Pickup starts on"
                    value={weekStart}
                    onChange={setWeekStart}
                    minDate={isoUpcomingMonday()}
                    normalize={isoMondayFromDate}
                    hint={`Meals will be scheduled starting Monday, ${weekStart}.`}
                  />
                )}
                {err && <MinimalAlert tone="danger">{err}</MinimalAlert>}
              </MinimalFieldGrid>
            </MinimalFormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <MinimalButton variant="secondary" onClick={() => navigate('/menu')}>
                Cancel
              </MinimalButton>
              <MinimalButton onClick={goNext}>Next: dishes</MinimalButton>
            </div>
          </StepGrid>
        )}

        {step === 'dishes' && (
          <StepGrid>
            <SectionHeading title={`Your dish library · ${library.length}`} />
            <DishPicker
              library={library}
              selectedIds={selectedDishes}
              onToggle={toggleDish}
            />
            <SectionDivider role="separator" aria-hidden />
            <SectionHeading title="Add a new dish" />
            <NewDishForm
              chefProfileId={profileId}
              onCreated={handleNewDish}
              existingNames={library.map((dish) => String(dish.name ?? ''))}
              categoryOptions={live ? kitchen.categories : DISH_CATEGORIES}
            />
            <CategoryRequestForm profileId={profileId} />
            <EndSentinel ref={dishesEndRef} aria-hidden />
            <ActionBar
              $elevated={dishesElevated}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <ActionBarMeta>
                <ChefStyle.Ref>
                  {selectedDishes.size} dish{selectedDishes.size === 1 ? '' : 'es'} selected
                </ChefStyle.Ref>
                <ChefStyle.Sub>Tap dishes above to add to this menu.</ChefStyle.Sub>
              </ActionBarMeta>
              <ActionBarActions>
                <MinimalButton variant="secondary" onClick={goBack}>
                  Back
                </MinimalButton>
                <MinimalButton onClick={goNext}>
                  Review →
                </MinimalButton>
              </ActionBarActions>
            </ActionBar>
          </StepGrid>
        )}

        {step === 'review' && (
          <StepGrid>
            <MinimalCard padding="sm" enter={false}>
              <ChefStyle.Ref>{title || 'Untitled menu'}</ChefStyle.Ref>
              <br />
              <ChefStyle.Sub>
                {cadence}
                {cadence === 'weekly' ? ` · wk ${weekStart}` : ''} · {selectedDishes.size} dish
                {selectedDishes.size === 1 ? '' : 'es'}
              </ChefStyle.Sub>
            </MinimalCard>

            <SectionHeading title="Selected dishes" />
            <ChefStyle.Stack>
              {selectedDishRecords.map((d) => (
                <MinimalCard key={String(d.id)} padding="sm" enter={false}>
                  <ChefStyle.ThumbRow>
                    <Thumb $src={d.image_url ? String(d.image_url) : undefined} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ChefStyle.Ref>{String(d.name ?? '')}</ChefStyle.Ref>
                      <br />
                      <ChefStyle.Sub>{enumLabel(d.category, 'main')}</ChefStyle.Sub>
                    </div>
                    <Money
                      amount={Number(d.price_minor ?? 0) / 100}
                      tone="brand"
                      size="sm"
                    />
                  </ChefStyle.ThumbRow>
                  <MinimalNumberField min={0} max={1000000} step={1}
                    label={`Total portions · ${String(d.name)}`}
                    placeholder="No limit"
                    hint="Maximum portions to sell. Leave empty for no limit."
                    value={optionalNumber(stockQuantities[String(d.id)] ?? d.stock_quantity)}
                    onValueChange={(value) => setStockQuantities((current) => ({ ...current, [String(d.id)]: value == null ? '' : String(value) }))} />
                </MinimalCard>
              ))}
            </ChefStyle.Stack>

            <MinimalCard padding="sm" enter={false}>
              <ChefStyle.Row>
                <ChefStyle.Ref>Combined dish price</ChefStyle.Ref>
                <Money amount={total / 100} tone="brand" size="md" />
              </ChefStyle.Row>
              <ChefStyle.Sub>
                Food prices appear in the cart. Order service fees are shown at checkout.
              </ChefStyle.Sub>
            </MinimalCard>

            {err && <MinimalAlert tone="danger">{err}</MinimalAlert>}

            <EndSentinel ref={reviewEndRef} aria-hidden />
            <ActionBar
              $elevated={reviewElevated}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <ActionBarMeta>
                <ChefStyle.Ref>{title || 'Untitled menu'}</ChefStyle.Ref>
                <ChefStyle.Sub>
                  {selectedDishes.size} dish{selectedDishes.size === 1 ? '' : 'es'} ·
                  basket <Money amount={total / 100} tone="brand" size="sm" />
                </ChefStyle.Sub>
              </ActionBarMeta>
              <ActionBarActions>
                {editing && enumShort(existingMenu?.status, 'draft') !== 'draft' && (
                  <MinimalButton variant="ghost" onClick={() => saveMenu('draft')} disabled={submitting || cover.uploading || Boolean(cover.error)}>
                    Save as draft
                  </MinimalButton>
                )}
                <MinimalButton variant="secondary" onClick={saveDraft} disabled={submitting || cover.uploading || Boolean(cover.error)}>
                  {submitting ? 'Saving…' : editing ? 'Save changes' : 'Save draft'}
                </MinimalButton>
                <MinimalButton onClick={publish} disabled={submitting || cover.uploading || Boolean(cover.error) || selectedDishes.size === 0}>
                  {submitting ? 'Publishing…' : 'Publish menu'}
                </MinimalButton>
              </ActionBarActions>
            </ActionBar>
          </StepGrid>
        )}
      </motion.div>
    </Screen>
  )
}

/** MinimalCalendar emits a Date; we want the Monday of the chosen week. */
const isoMondayFromDate = (d: Date) => isoMonday(d)
